<script lang="ts">
  // Svelte / Kit
  import { beforeNavigate } from '$app/navigation';
  import type { BeforeNavigate } from '@sveltejs/kit';
  // UI components
  import * as Accordion from '$lib/components/ui/accordion/index.js';
  import { Button } from '$lib/components/ui/button';
  import * as Dialog from '$lib/components/ui/dialog';
  import { Label } from '$lib/components/ui/label/index.js';
  import * as Select from '$lib/components/ui/select/index.js';
  import { Separator } from '$lib/components/ui/separator';
  import Switch from '$lib/components/ui/switch/switch.svelte';
  import * as Tooltip from '$lib/components/ui/tooltip/index.js';
  import { LoaderCircle, OctagonAlert } from '@lucide/svelte';
  // RoastLog
  import { updateRoasterSettings } from '$lib/api';
  import DeviceIcon from '$lib/components/DeviceIcon.svelte';
  import DeviceSettings from '$lib/components/settings/DeviceSettings.svelte';
  import HeatingRateControls from '$lib/components/settings/HeatingRateControls.svelte';
  import LabelsSettings from '$lib/components/settings/LabelsSettings.svelte';
  import ModbusDebugControls from '$lib/components/settings/ModbusDebugControls.svelte';
  import TCSettings from '$lib/components/settings/TCSettings.svelte';
  import { DeviceType } from '$lib/constants';
  import { getAvailableDevices } from '$lib/devices/registry';
  import {
    clearStateForLocationChange,
    isDemo,
    isNewLogin,
    locations,
    plotBurner,
    roasters,
    selectedDeviceType,
    selectedLocation,
    selectedRoaster
  } from '$lib/PersistedState.svelte';
  import { globalState, initialDataLoad } from '$lib/State.svelte';
  import type { SelectChoice } from '$lib/types/RoastLog';
  // others
  import FakeButton from '$lib/components/FakeButton.svelte';
  import logger from '$lib/logger';
  import { toast } from 'svelte-sonner';
  import StartStopShortcutControl from '$lib/components/settings/StartStopShortcutControl.svelte';
  import ReferenceRoastEventDisplayControl from '$lib/components/settings/ReferenceRoastEventDisplayControl.svelte';
  import LiveStreamControl from '$lib/components/settings/LiveStreamControl.svelte';
  import { fetchAndStoreDataByLocation, fetchAndStoreRoasters } from '$lib/dataLoader';
  import { MassScale } from '$lib/types/RoastLog';

  let loadingLocation = $state(false);

  let selectedLocationName = $derived.by(() => {
    if (selectedLocation.current && locations.current.length > 0) {
      const locationId = parseInt(selectedLocation.current);
      const location = locations.current.find((l) => l.id === locationId);
      if (location) {
        return location.name;
      }
    }
    return 'Select a location...';
  });

  let locationChoices = $derived(
    locations.current.map(
      (r): SelectChoice => ({
        value: r.id.toString(),
        label: r.name
      })
    )
  );

  const handleLocationChanged = async (value: string) => {
    // We need to clear a bunch of things out to get started

    logger.info(`Changing location to: ${value}`);

    loadingLocation = true;
    clearStateForLocationChange();

    selectedLocation.current = value;

    try {
      await fetchAndStoreDataByLocation(Number(value));

      const roasterIds = Object.keys(roasters.current);

      if (roasterIds.length > 0) {
        const firstRoaster = roasters.current[roasterIds[0]];
        logger.info(`Location change. Setting roaster to ${firstRoaster.name}`);
        selectedRoaster.roasterId = firstRoaster.id;
      } else {
        logger.info('Location change. Did not find roasters', { location: selectedLocationName });
      }
    } catch (error) {
      logger.error('Error loading location data', { error });
    }
    loadingLocation = false;
  };

  const handleRoasterChanged = (value: string) => {
    selectedRoaster.roasterId = value;
    logger.info(`Roaster changed: ${selectedRoaster.name}`);
  };

  let roasterChoices = $derived(
    Object.values(roasters.current).map((r) => ({
      value: r.id,
      label: r.name
    }))
  );

  beforeNavigate(async (nav: BeforeNavigate) => {
    // When someone logs out, this may be triggered and the store is null. So, add this
    // conditional.
    if (!selectedRoaster.roasterId) {
      logger.warn('Selected roaster not set in settings');
      return;
    }

    const isValid = validateTCSettings();

    if (isValid) {
      const tcSettings: any = {};
      selectedRoaster.tcSettings.current.forEach((tc, i) => {
        // Note, TC settings on the server are 1-based
        tcSettings[(i + 1).toString()] = tc;
      });

      logger.info('Updating roaster settings', {
        tcSettings,
        massScale: selectedRoaster.massScale
      });
      updateRoasterSettings(selectedRoaster.roasterId, {
        thermocouples: tcSettings,
        mass_scale: selectedRoaster.massScale
      });
    } else {
      nav.cancel();
      toast.error('Please ensure each thermocouple has a valid name.');
    }
  });

  const validateTCSettings = (): boolean => {
    if (selectedDeviceType.hasHardcodedLabels) {
      return true;
    }

    return selectedRoaster.tcSettings.current.every((r) => {
      return !r.active || (r.active && r.name && r.name !== '');
    });
  };

  const handleDismissNewLogin = (): void => {
    isNewLogin.reset();
  };

  const handleDeviceTypeChanged = (v: string) => {
    // Check if this is the currently selected item. The select box doesn't stop you from selecting
    // the current value.
    logger.info(`User changed device type: ${v}`);
    if (globalState.deviceConnected) {
      globalState.deviceConnected = false;
    }
    selectedDeviceType.value = v as DeviceType;
  };

  // @ts-ignore
  const isTauri = window.__TAURI_INTERNALS__;
</script>

{#if initialDataLoad.current}
  <div
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 backdrop-blur-xs dark:bg-slate-900/50"
  >
    <LoaderCircle class="h-24 w-24 animate-spin text-white dark:text-slate-100" />
    <div class="mt-4 animate-pulse text-xl text-slate-200">
      <span>Fetching and loading initial data...</span>
    </div>
  </div>
{:else if loadingLocation}
  <div
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 backdrop-blur-xs dark:bg-slate-900/50"
  >
    <LoaderCircle class="h-24 w-24 animate-spin text-white dark:text-slate-100" />
    <div class="mt-4 animate-pulse text-xl text-slate-200">
      <span>Loading data from {selectedLocationName}...</span>
    </div>
  </div>
{:else if isNewLogin.current}
  <Dialog.Root
    open={isNewLogin.current}
    onOpenChange={(open) => {
      if (!open) {
        handleDismissNewLogin();
      }
    }}
  >
    <Dialog.Content escapeKeydownBehavior="ignore">
      <Dialog.Header>
        <Dialog.Title class="mb-5">Great to see you here!</Dialog.Title>
        <Dialog.Description
          >Please confirm your setup before continuing. Check that you've selected the correct
          device and roaster, and review all other settings to ensure they match your preferences.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Footer>
        <Button type="submit" onclick={handleDismissNewLogin}>Dismiss</Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog.Root>
{/if}

<div class="bg-card h-[calc(100dvh-128px)] overflow-y-auto rounded-lg p-5">
  <div class="grid grid-cols-2 items-center justify-items-start gap-2">
    <div class="w-3/4">
      <Label for="location">Location</Label>

      {#if locationChoices.length > 1}
        <Select.Root
          type="single"
          value={selectedLocation.current ? selectedLocation.current : undefined}
          onValueChange={(value) => handleLocationChanged(value)}
        >
          <Select.Trigger class="w-full">{selectedLocationName}</Select.Trigger>
          <Select.Content>
            {#each locationChoices as location}
              <Select.Item value={location.value} label={location.label}>
                {location.label}
              </Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      {:else}
        <FakeButton>{selectedLocationName}</FakeButton>
      {/if}
    </div>

    <div class="w-3/4">
      <Label for="roaster">Roaster</Label>

      <Select.Root
        type="single"
        value={selectedRoaster.roasterId ? selectedRoaster.roasterId : undefined}
        onValueChange={(value) => handleRoasterChanged(value)}
      >
        <Select.Trigger class="w-full">{selectedRoaster.name}</Select.Trigger>
        <Select.Content>
          {#each roasterChoices as roaster}
            <Select.Item value={roaster.value} label={roaster.label}>
              {roaster.label}
            </Select.Item>
          {/each}
        </Select.Content>
      </Select.Root>
    </div>

    <div class="w-3/4">
      <Label>Device type</Label>
      <Select.Root
        type="single"
        value={selectedDeviceType.value ? selectedDeviceType.value : undefined}
        onValueChange={(value) => handleDeviceTypeChanged(value)}
      >
        <Select.Trigger class="w-full">{selectedDeviceType.label}</Select.Trigger>
        <Select.Content>
          {#each getAvailableDevices(isTauri, isDemo.current) as deviceConfig}
            <Select.Item
              value={deviceConfig.type}
              disabled={selectedDeviceType.isType(deviceConfig.type)}
            >
              <DeviceIcon deviceType={deviceConfig.type} />
              <span class="ml-1">
                {deviceConfig.label}
              </span>
            </Select.Item>
          {/each}
        </Select.Content>
      </Select.Root>
    </div>

    <div class="w-3/4">
      <Label for="mass-scale">Mass scale for this roaster</Label>
      <Select.Root
        type="single"
        value={selectedRoaster.massScale}
        onValueChange={(value) => {
          selectedRoaster.massScale = value as MassScale;
        }}
      >
        <Select.Trigger class="w-full">
          {selectedRoaster.massScale === MassScale.Pounds
            ? 'Pounds (lb)'
            : selectedRoaster.massScale === MassScale.Kilograms
              ? 'Kilograms (kg)'
              : 'Grams (g)'}
        </Select.Trigger>
        <Select.Content>
          <Select.Item value={MassScale.Pounds} label="Pounds (lb)">Pounds (lb)</Select.Item>
          <Select.Item value={MassScale.Kilograms} label="Kilograms (kg)">
            Kilograms (kg)
          </Select.Item>
          <Select.Item value={MassScale.Grams} label="Grams (g)">Grams (g)</Select.Item>
        </Select.Content>
      </Select.Root>
    </div>

    {#if selectedDeviceType.hasBurnerChart}
      <div class="w-3/4">
        <Label id="plot-burner" for="burner-switch">Plot burner?</Label>
        <Switch
          id="burner-switch"
          aria-labelledby="plot-burner-label"
          bind:checked={plotBurner.current}
        />
      </div>
    {/if}
  </div>

  <div class="pt-5">
    {#if selectedRoaster.roasterId}
      <LabelsSettings />
      {#if selectedDeviceType.hasHardcodedLabels}
        <DeviceSettings />
      {:else}
        <TCSettings />
        <DeviceSettings />
      {/if}
    {/if}
  </div>

  <div class="mt-10 flex flex-row items-center gap-2">
    <div class="text-xl">Advanced settings</div>

    <Tooltip.Provider delayDuration={250}>
      <Tooltip.Root>
        <Tooltip.Trigger>
          <OctagonAlert class="stroke-yellow-500 dark:stroke-red-600" />
        </Tooltip.Trigger>
        <Tooltip.Content class="bg-yellow-100 dark:bg-red-800" side="right"
          >Warning: Only adjust these settings if you know what they do!</Tooltip.Content
        >
      </Tooltip.Root>
    </Tooltip.Provider>
  </div>

  <Separator class="mt-2" />

  <Accordion.Root class="w-2/3 px-5" type="single">
    <HeatingRateControls />
    <StartStopShortcutControl />
    <ReferenceRoastEventDisplayControl />
    <LiveStreamControl />
    {#if selectedDeviceType.hasModbusSettings}
      <ModbusDebugControls />
    {/if}
  </Accordion.Root>
</div>
